import {BasicTaDetails} from '../api';
import {SearchTaList} from './app.types';

// How long a cached TA list is trusted before its tab re-requests page 0. Other users change these
// lists and this session cannot observe that, so the cache needs an expiry.
export const LIST_CACHE_TTL_MS = 300_000;

export function isListCacheFresh(cache: SearchTaList | undefined): boolean {
  return cache?.loadedAt !== undefined && Date.now() - cache.loadedAt < LIST_CACHE_TTL_MS;
}

// Fields a lifecycle action can change that the list tables render. `approvals` is deliberately
// absent: ObjectMapper.ToBasicTaDetails never populates it, so it arrives empty and would blank the
// chain the cached row already holds. `currentValue`/`originalValue` are absent for the same reason.
const WRITE_THROUGH_FIELDS = [
  'status', 'startDate', 'endDate', 'taNumber', 'projectCode', 'projectId', 'projectApprover',
  'primaryTraveller', 'travellerCount', 'mainDestinationLocationId', 'mainDestinationLocationNameId',
  'mainDestinationLocationName', 'mainDestinationLocationCode', 'isPrePaid', 'prePaymentProcessed',
  'delegate',
] as const satisfies readonly (keyof BasicTaDetails)[];

/**
 * Merges a changed TA into one cached list. A cache that does not already hold the row is returned
 * untouched — deciding whether it now belongs would mean re-implementing the endpoint's server-side
 * filter, and inserting would mean inventing a sort position and a totalCount.
 */
export function reconcileCachedTa(
  cache: SearchTaList | undefined,
  changed: BasicTaDetails,
  stillBelongs: boolean,
): {cache: SearchTaList | undefined; removed: boolean} {
  const index = cache?.taList?.findIndex(ta => ta.id === changed.id) ?? -1;
  if (!cache?.taList || index < 0) {
    return {cache, removed: false};
  }

  if (!stillBelongs) {
    return {
      cache: {
        ...cache,
        taList: cache.taList.filter((_, i) => i !== index),
        currentCount: Math.max(0, (cache.currentCount ?? 0) - 1),
        totalCount: Math.max(0, (cache.totalCount ?? 0) - 1),
      },
      removed: true,
    };
  }

  const patched = {...cache.taList[index]};
  for (const field of WRITE_THROUGH_FIELDS) {
    // Skipped when absent rather than spread wholesale: the server legitimately omits some of these
    // (projectApprover is conditional, prePaymentProcessed is null when not pre-paid) and a spread
    // would write undefined over a good cached value. Writing through a union key needs the cast.
    if (changed[field] !== undefined) {
      (patched as Record<string, unknown>)[field] = changed[field];
    }
  }
  const taList = [...cache.taList];
  taList[index] = patched;
  return {cache: {...cache, taList}, removed: false};
}
